import React, { useEffect, useState } from 'react';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const errText = e => {
  const d = e.response?.data?.detail;
  return typeof d === 'string' ? d : 'Something went wrong. Please try again.';
};
const inputCls = 'w-full px-4 py-3 border border-[#E2E2DF] rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]/30 focus:border-[#D35400]';
const btnCls = 'w-full py-3 bg-[#D35400] hover:bg-[#A04000] text-white font-semibold rounded-md text-sm transition-colors disabled:opacity-50';

function useApprovalPoll(onApproved, onError) {
  const { api } = useAuth();
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const { data } = await api.get('/auth/device-status');
        if (data.approved) { clearInterval(id); onApproved(); }
      } catch (e) {
        if ([401, 403, 409].includes(e.response?.status)) { clearInterval(id); onError(errText(e)); }
      }
    }, 5000);
    return () => clearInterval(id);
  }, [api, onApproved, onError]);
}

function SecretForm({ testid, label, type, placeholder, path, field, onApproved }) {
  const { api } = useAuth();
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async e => {
    e.preventDefault();
    setBusy(true); setErr('');
    try { await api.post(path, { [field]: val }); onApproved(); }
    catch (ex) { setErr(errText(ex)); setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`${testid}-form`}>
      <label className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">{label}</label>
      <input data-testid={`${testid}-input`} type={type} value={val} onChange={e => setVal(e.target.value)} placeholder={placeholder} className={inputCls} required />
      {err && <p className="text-sm text-red-600" data-testid={`${testid}-error`}>{err}</p>}
      <button data-testid={`${testid}-submit`} disabled={busy} className={btnCls}>{busy ? 'Checking...' : 'Continue'}</button>
    </form>
  );
}

export function DevicePending({ info, onCancel }) {
  const { completeLogin } = useAuth();
  const [fatal, setFatal] = useState('');
  const done = React.useCallback(() => { completeLogin(); }, [completeLogin]);
  useApprovalPoll(done, setFatal);
  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#F9F9F8]" data-testid="device-pending-screen">
      <div className="w-full max-w-md bg-white border border-[#E2E2DF] rounded-md p-8 space-y-6">
        <div className="flex items-center gap-3 text-[#D35400]"><ShieldCheck size={22} />
          <h2 className="text-xl font-extrabold text-slate-900 font-[Manrope]">New device</h2></div>
        <p className="text-sm text-slate-600">This is the first time <b>{info.device_label}</b> has been used with your account.
          {info.is_admin ? ' As an admin, confirm your password to trust it.' : ' An admin needs to approve it before you can continue.'}</p>
        {fatal ? <p className="text-sm text-red-600" data-testid="device-pending-error">{fatal}</p> : info.is_admin ? (
          <SecretForm testid="device-self-approve" label="Confirm password" type="password" placeholder="Your password" path="/auth/device-self-approve" field="password" onApproved={done} />
        ) : (
          <>
            <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="device-pending-waiting"><Loader2 size={16} className="animate-spin" />Waiting for an admin to approve…</div>
            <SecretForm testid="device-code" label="Or enter the 6-digit code from your admin" type="text" placeholder="123456" path="/auth/device-code" field="code" onApproved={done} />
          </>
        )}
        <button data-testid="device-pending-cancel" onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-800 transition-colors">Back to login</button>
      </div>
    </div>
  );
}
